import { getConnectionContactName } from "@/lib/utils/connections/item-contact-connections";
import { Badge, Button, DataTableColumnHeader } from "@/components/ui";
import type {
  FirmSignatureRequestListItemV2,
  MatterSignatureRequestListItemV2,
} from "@/lib/data/requests/signature-requests/signature-request";
import type {
  MatterContactConnection,
  MatterUserConnection,
} from "@/lib/types";
import { withSignatureRequestInvitations } from "@/lib/utils/connections/workflow-contact-connections";
import {
  formatContactsName,
  formatLocalOrIntlPhoneNumber,
  formatUsersName,
} from "@/lib/utils/format";
import type { ColumnDef } from "@tanstack/react-table";
import { Copy, Loader2, Mail, MailCheck, MessageSquare } from "lucide-react";
import toast from "react-hot-toast";

export type SignatureRequestRecipientKind = "contact" | "user";

export type SignatureRequestRecipientRow = {
  kind: SignatureRequestRecipientKind;
  order: number | undefined;
  contactConnection: MatterContactConnection | null;
  userConnection: MatterUserConnection | null;
  displayName: string;
  email: string;
  phoneNumber: string;
  phoneExtension: string | null;
  passcode: string;
  isSigned: boolean;
  pendingSigner?: boolean;
  sourceIndex: number;
  label: string;
};

type BuildRecipientRowsOptions = {
  contactSigners: Array<
    MatterContactConnection & {
      signerOrder?: number | null;
      pendingSigner?: boolean;
      matterSignatureRequestSignerOrder?: number | null;
      matterSignatureRequestPendingSigner?: boolean;
    }
  >;
  userSigners: Array<
    MatterUserConnection & {
      signerOrder?: number | null;
      pendingSigner?: boolean;
      matterSignatureRequestSignerOrder?: number | null;
      matterSignatureRequestPendingSigner?: boolean;
    }
  >;
};

type SignatureRequestRecipientColumnsOptions = {
  expirationDate?: string;
  showOrderColumn?: boolean;
  onSendContactText?: (row: SignatureRequestRecipientRow) => void;
  onSendContactEmail?: (row: SignatureRequestRecipientRow) => void;
  onSendUserEmail?: (row: SignatureRequestRecipientRow) => Promise<void>;
  contactEmailSent?: Record<string, boolean>;
  userEmailSent?: Record<string, boolean>;
  userEmailSending?: Record<string, boolean>;
  canSendUserEmails?: boolean;
};

const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Passcode copied to clipboard");
  } catch {
    toast.error("Failed to copy passcode");
  }
};

const getRecipientStatus = (
  isSigned: boolean,
  expirationDate?: string,
  pendingSigner?: boolean,
) => {
  if (isSigned) {
    return { label: "Signed", variant: "green" as const };
  }

  if (
    expirationDate &&
    new Date().getTime() > new Date(expirationDate).getTime()
  ) {
    return { label: "Expired", variant: "destructive" as const };
  }

  if (pendingSigner === false) {
    return { label: "Pending", variant: "warning" as const };
  }

  return { label: "Pending", variant: "warning" as const };
};

export const buildSignatureRequestRecipientRows = ({
  contactSigners,
  userSigners,
}: BuildRecipientRowsOptions): SignatureRequestRecipientRow[] => {
  const contactRows: SignatureRequestRecipientRow[] = contactSigners.map(
    (signer, sourceIndex) => {
      const label = signer.label;
      const displayName =
        formatContactsName(signer.contact) || "Unnamed contact";
      const email = signer.contact.emails[0]?.address ?? "";
      const primaryPhone = signer.contact.phones.at(0);

      return {
        kind: "contact" as const,
        order:
          signer.signerOrder ??
          signer.matterSignatureRequestSignerOrder ??
          undefined,
        contactConnection: signer,
        userConnection: null,
        displayName,
        email,
        phoneNumber: primaryPhone?.number ?? "",
        phoneExtension: primaryPhone?.extension ?? null,
        passcode: signer.signatureRequestPasscode || "",
        isSigned: signer.signatureRequestComplete === true,
        pendingSigner:
          signer.pendingSigner ?? signer.matterSignatureRequestPendingSigner,
        sourceIndex,
        label,
      };
    },
  );

  const userRows: SignatureRequestRecipientRow[] = userSigners.map(
    (signer, sourceIndex) => {
      const label = signer.label ?? "";
      const displayName = formatUsersName(signer) || "Unnamed firm signer";
      const email = signer.email ?? "";
      const phoneNumber = signer.phoneNumber ?? "";

      return {
        kind: "user" as const,
        order:
          signer.signerOrder ??
          signer.matterSignatureRequestSignerOrder ??
          undefined,
        contactConnection: null,
        userConnection: signer,
        displayName,
        email,
        phoneNumber,
        phoneExtension: null,
        passcode: signer.signatureRequestPasscode || "",
        isSigned: signer.signatureRequestComplete === true,
        pendingSigner:
          signer.pendingSigner ?? signer.matterSignatureRequestPendingSigner,
        sourceIndex,
        label,
      };
    },
  );

  return [...contactRows, ...userRows].sort((a, b) => {
    if (a.order != null && b.order != null) {
      if (a.order !== b.order) return a.order - b.order;
    } else if (a.order != null) {
      return -1;
    } else if (b.order != null) {
      return 1;
    }
    if (a.kind !== b.kind) {
      return a.kind === "contact" ? -1 : 1;
    }
    return a.sourceIndex - b.sourceIndex;
  });
};

type V2SignatureRequest =
  | FirmSignatureRequestListItemV2
  | MatterSignatureRequestListItemV2;

/** Builds recipient rows from the V2 request payload without legacy connection adapters. */
export const buildV2SignatureRequestRecipientRows = (
  request: V2SignatureRequest,
): SignatureRequestRecipientRow[] => {
  if ("matterId" in request) {
    return [
      ...request.connections.map((signer, sourceIndex) => ({
        kind: "contact" as const,
        order: signer.signerOrder ?? undefined,
        contactConnection: null,
        userConnection: null,
        displayName:
          [
            signer.contact?.individualFirstName,
            signer.contact?.individualLastName,
          ]
            .filter(Boolean)
            .join(" ") ||
          getConnectionContactName(signer.contact) ||
          "Unnamed contact",
        email: signer.contact?.emails?.[0]?.address ?? "",
        phoneNumber: signer.contact?.phones?.[0]?.number ?? "",
        phoneExtension: signer.contact?.phones?.[0]?.extension ?? null,
        passcode:
          request.invitations?.find((item) => item.connectionId === signer.id)
            ?.passcode ?? "",
        isSigned: signer.signatureRequestComplete ?? false,
        pendingSigner: signer.pending ?? false,
        sourceIndex,
        label: "Signer",
      })),
      ...withSignatureRequestInvitations(
        request.userConnections ?? [],
        request.invitations,
      ).map((signer, sourceIndex) => ({
        kind: "user" as const,
        order: signer.signerOrder ?? undefined,
        contactConnection: null,
        userConnection: null,
        displayName:
          [signer.firstName, signer.lastName].filter(Boolean).join(" ") ||
          "Unnamed firm signer",
        email: signer.email ?? "",
        phoneNumber: signer.phoneNumber ?? "",
        phoneExtension: null,
        passcode: signer.signatureRequestPasscode,
        isSigned: signer.signatureRequestComplete,
        pendingSigner: signer.pendingSigner,
        sourceIndex,
        label: signer.label,
      })),
    ].sort(
      (a, b) =>
        (a.order ?? Number.POSITIVE_INFINITY) -
        (b.order ?? Number.POSITIVE_INFINITY),
    );
  }

  const contactRows = request.contactConnections.map((signer, sourceIndex) => ({
    kind: "contact" as const,
    order: signer.signerOrder ?? undefined,
    contactConnection: null,
    userConnection: null,
    displayName: signer.contactName || "Unnamed contact",
    email: signer.contactEmails[0]?.emailAddress ?? "",
    phoneNumber: signer.contactPhones[0]?.number ?? "",
    phoneExtension: signer.contactPhones[0]?.extension ?? null,
    passcode:
      request.invitations?.find((item) => item.connectionId === signer.id)
        ?.passcode ?? "",
    isSigned: signer.signatureRequestComplete,
    pendingSigner: signer.pendingSigner,
    sourceIndex,
    label: signer.label,
  }));
  const userRows = withSignatureRequestInvitations(
    request.userConnections,
    request.invitations,
  ).map((signer, sourceIndex) => {
    const user = signer.user;
    return {
      kind: "user" as const,
      order: signer.signerOrder ?? undefined,
      contactConnection: null,
      userConnection: null,
      displayName:
        [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
        "Unnamed firm signer",
      email: user?.email ?? "",
      phoneNumber: "",
      phoneExtension: null,
      passcode: signer.signatureRequestPasscode,
      isSigned: signer.signatureRequestComplete,
      pendingSigner: signer.pendingSigner,
      sourceIndex,
      label: signer.label,
    };
  });
  return [...contactRows, ...userRows].sort(
    (a, b) =>
      (a.order ?? Number.POSITIVE_INFINITY) -
      (b.order ?? Number.POSITIVE_INFINITY),
  );
};

export const signatureRequestRecipientColumns = ({
  expirationDate,
  showOrderColumn = false,
  onSendContactText,
  onSendContactEmail,
  onSendUserEmail,
  contactEmailSent,
  userEmailSent,
  userEmailSending,
  canSendUserEmails = true,
}: SignatureRequestRecipientColumnsOptions): ColumnDef<SignatureRequestRecipientRow>[] => {
  const orderColumn: ColumnDef<SignatureRequestRecipientRow> = {
    accessorKey: "order",
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    id: "Order",
    cell: ({ getValue }) => {
      const order = getValue() as number | undefined;
      return order != null ? (
        <div className="text-sm font-medium">{order}</div>
      ) : null;
    },
  };

  return [
    ...(showOrderColumn ? [orderColumn] : []),
    {
      accessorKey: "kind",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      id: "Type",
      cell: ({ row }) => (
        <Badge variant={row.original.kind === "contact" ? "blue" : "purple"}>
          {row.original.kind === "contact" ? "Contact" : "Firm"}
        </Badge>
      ),
    },
    {
      accessorKey: "displayName",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      id: "Signer",
      cell: ({ getValue }) => (
        <div className="max-w-full truncate text-sm font-medium">
          {getValue() as string}
        </div>
      ),
    },
    {
      accessorKey: "email",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      id: "Email",
      cell: ({ getValue }) => {
        const email = getValue() as string;
        return (
          <div className="text-muted-foreground truncate text-sm">
            {email || "No email"}
          </div>
        );
      },
    },
    {
      accessorKey: "phoneNumber",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      id: "Phone",
      cell: ({ row }) => {
        const phoneNumber = row.original.phoneNumber;
        return (
          <div className="text-muted-foreground truncate text-sm">
            {phoneNumber
              ? formatLocalOrIntlPhoneNumber({
                  number: phoneNumber,
                  extension: row.original.phoneExtension,
                }) || phoneNumber
              : "No phone number"}
          </div>
        );
      },
    },
    {
      accessorKey: "isSigned",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      id: "Status",
      cell: ({ row }) => {
        const status = getRecipientStatus(
          row.original.isSigned,
          expirationDate,
          row.original.pendingSigner,
        );
        return <Badge variant={status.variant}>{status.label}</Badge>;
      },
    },
    {
      accessorKey: "passcode",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      id: "Actions",
      cell: ({ row, getValue }) => {
        const passcode = getValue() as string;
        const link = passcode
          ? `${window.location.origin}/sign?passcode=${passcode}`
          : "";
        const contactId = row.original.contactConnection?.contactId;
        const id = row.original.userConnection?.userId;
        const contactEmailAlreadySent = contactId
          ? contactEmailSent?.[contactId]
          : false;
        const userEmailAlreadySent = id ? userEmailSent?.[id] : false;
        const userEmailIsSending = id ? userEmailSending?.[id] : false;

        return (
          <div className="flex items-center gap-2">
            {passcode && (
              <Button
                data-testid={
                  row.original.kind === "contact"
                    ? "copy-passcode-button"
                    : "user-copy-passcode-button"
                }
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  void copyToClipboard(link);
                }}
                className="h-6 w-6 p-0"
              >
                <Copy className="h-3 w-3" />
              </Button>
            )}
            {row.original.kind === "contact" ? (
              <>
                {onSendContactText && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      onSendContactText(row.original);
                    }}
                    disabled={!row.original.phoneNumber || !link}
                    title={
                      row.original.phoneNumber
                        ? "Text link"
                        : "No phone available"
                    }
                    className="h-6 w-6 p-0"
                  >
                    <MessageSquare className="h-3 w-3" />
                  </Button>
                )}
                {onSendContactEmail && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      onSendContactEmail(row.original);
                    }}
                    disabled={
                      !row.original.email || !link || contactEmailAlreadySent
                    }
                    title={
                      contactEmailAlreadySent
                        ? "Email sent"
                        : row.original.email
                          ? "Email link"
                          : "No email available"
                    }
                    className="h-6 w-6 p-0"
                  >
                    {contactEmailAlreadySent ? (
                      <MailCheck className="h-3 w-3 text-green-500" />
                    ) : (
                      <Mail className="h-3 w-3" />
                    )}
                  </Button>
                )}
              </>
            ) : onSendUserEmail ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  void onSendUserEmail(row.original);
                }}
                disabled={
                  !canSendUserEmails ||
                  !row.original.email ||
                  !link ||
                  userEmailAlreadySent ||
                  userEmailIsSending
                }
                title={
                  !canSendUserEmails
                    ? "You don't have permission to email users"
                    : userEmailAlreadySent
                      ? "Email sent"
                      : row.original.email
                        ? "Email link"
                        : "No email available"
                }
                className="h-6 w-6 p-0"
              >
                {userEmailAlreadySent ? (
                  <MailCheck className="h-3 w-3 text-green-500" />
                ) : userEmailIsSending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Mail className="h-3 w-3" />
                )}
              </Button>
            ) : null}
          </div>
        );
      },
    },
  ];
};
